import { Classes, Dialog, InputGroup, Position, Tooltip } from "@blueprintjs/core";
import { observer } from "mobx-react";
import { Buttons } from "modules/common/components/form";
import { StandardFormInput } from "modules/common/components/form/StandardFormInput";
import { PlanrButton } from "modules/common/components/planr/button/Button";
import { GeneralIcon } from "modules/common/components/planr/icon/Generalcon";
import { getFieldLabel } from "modules/common/services/form/fields";
import { onlyDigitsInputKeyPress } from "modules/common/services/form/values";
import { texts } from "modules/common/texts";
import React from "react";
import { Schema } from "yup";
import { isNetworkAddress } from "../validation";
import { AutomaticNetworkSnapshotType, BonusSalaryPercentType, fields, SettingsStoreType } from "../models/settings";
import styles from "./SystemSettings.module.scss";

import { DictionaryLinkType } from "modules/common/models/dictionary-link";

const digits = onlyDigitsInputKeyPress();

class SystemBase extends React.PureComponent<SystemSettingsProps, SystemState> {
  constructor(props: any) {
    super(props);

    this.state = {
      showdialog: false,
      showDanger: false,
      bonusSalaryPercent: [],
      employersExclude: [],
      showNetworksDialog: false,
      networks: [],
      networksError: false,
    };
  }

  render() {
    const {
      schema,
      store,
      showDialog,
      toggleShowDialog,
      sendMailTest,
      topic,
      text,
      emailTo,
      setMail,
      setTopic,
      setMessage,
    } = this.props;
    const { showNetworksDialog, networks, networksError } = this.state;

    return (
      <div className="columns">
        <div className={styles.columns}>
          <div className={styles.column}>
            <StandardFormInput
              name={fields.automaticNetworks}
              schema={schema}
              small={true}
              className={styles.full}
              inline={false}
              label={null}
            >
              {({ field, form }) => (
                <>
                  <PlanrButton
                    type="secondary"
                    size="small"
                    style={{ marginBottom: "10px" }}
                    onClick={() => {
                      this.setState({ networks: [...(field.value || [])], networksError: false });
                      this.toggleNetworksDialog();
                    }}
                  >
                    {getFieldLabel(fields.automaticNetworks, schema, null)}
                  </PlanrButton>

                  <Dialog
                    title="IP-адреса для автоматического входа"
                    isOpen={showNetworksDialog}
                    isCloseButtonShown={true}
                    onClose={this.toggleNetworksDialog}
                    backdropClassName="standard"
                    style={{ width: "600px" }}
                    className="figma-dialog"
                  >
                    <div className={Classes.DIALOG_BODY}>
                      <div className={styles.dialogInfo}>
                        <GeneralIcon type="general-info" style={{ marginRight: "10px" }} />
                        Вход с этих адресов не требует подтверждения устройства
                      </div>

                      {networks.length > 0 && (
                        <div className={styles.itemRowHeaderWrap}>
                          <div className={styles.networkAddress}>Адрес или подсеть</div>
                          <div className={styles.networkComment}>Комментарий</div>
                        </div>
                      )}

                      {networks.map((network, index) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <div className={styles.networkRow} key={index}>
                          <InputGroup
                            className={`planr-default-input ${styles.networkAddress}`}
                            value={network.address}
                            placeholder="192.168.0.1 или 192.168.0.0/24"
                            type="text"
                            autoComplete="off"
                            data-lpignore="true"
                            onChange={(e: React.FormEvent<HTMLInputElement>) => {
                              this.setNetwork(index, "address", e.currentTarget.value);
                            }}
                          />
                          <InputGroup
                            className={`planr-default-input ${styles.networkComment}`}
                            value={network.comment}
                            placeholder="Комментарий"
                            type="text"
                            autoComplete="off"
                            data-lpignore="true"
                            onChange={(e: React.FormEvent<HTMLInputElement>) => {
                              this.setNetwork(index, "comment", e.currentTarget.value);
                            }}
                          />
                          <PlanrButton
                            type="neutral"
                            icon="general-trash"
                            size="small"
                            title={texts.remove}
                            onClick={() => this.removeNetwork(index)}
                          />
                        </div>
                      ))}

                      <div style={{ marginTop: "6px" }}>
                        <PlanrButton size="small" type="secondary" icon="general-plus-big" onClick={this.addNetwork}>
                          Добавить
                        </PlanrButton>
                      </div>

                      {networksError && (
                        <div style={{ color: "red", marginTop: "10px", fontSize: "12px" }}>
                          Укажите корректный IP-адрес (192.168.0.1) или подсеть (192.168.0.0/24)!
                        </div>
                      )}
                    </div>

                    <div className={Classes.DIALOG_FOOTER}>
                      <Buttons
                        left={
                          <div style={{ display: "flex" }}>
                            <PlanrButton
                              size="small"
                              type="greenish"
                              onClick={async () => {
                                const hasError = networks.some((network) => !isNetworkAddress(network.address));

                                if (hasError) {
                                  this.setState({ networksError: true });
                                  return;
                                }

                                // список уходит на сервер сразу, не дожидаясь сохранения всей формы
                                const saved = await store.saveAutomaticNetworks(networks);

                                if (!saved) {
                                  return;
                                }

                                form.setFieldValue(field.name, saved);
                                form.setFieldTouched(field.name, true);
                                this.toggleNetworksDialog();
                              }}
                            >
                              Сохранить
                            </PlanrButton>
                            <PlanrButton size="small" type="graybtn" onClick={this.toggleNetworksDialog}>
                              Отмена
                            </PlanrButton>
                          </div>
                        }
                      />
                    </div>
                  </Dialog>
                </>
              )}
            </StandardFormInput>
            <StandardFormInput
              name={fields.administratorPositions}
              schema={schema}
              small={true}
              className="planr-form-input"
              inline={false}
              label={
                <span className="planr-input-label-help">
                  {getFieldLabel(fields.administratorPositions, schema, null)} (или несколько через запятую)&nbsp;&nbsp;
                  <Tooltip
                    content="Используется для рассылки уведомлений системным администраторам"
                    position={Position.RIGHT}
                  >
                    <GeneralIcon type="general-question" />
                  </Tooltip>
                </span>
              }
            >
              {({ field }) => (
                <InputGroup
                  {...field}
                  id={field.name}
                  className="planr-default-input"
                  type="text"
                  autoComplete="off"
                  data-lpignore="true"
                />
              )}
            </StandardFormInput>
            <StandardFormInput
              name={fields.sendEmail}
              schema={schema}
              inline={false}
              small={true}
              className="planr-form-input"
            >
              {({ field }) => (
                <InputGroup
                  id={field.name}
                  className="planr-default-input"
                  {...field}
                  type="text"
                  autoComplete="off"
                  data-lpignore="true"
                />
              )}
            </StandardFormInput>
            {/*
              Пароль почты прячем так же, как пароль 1С. Осознанно НЕ используем
              type="password": браузер принимает пару «текстовое поле + password»
              внутри общей формы настроек за форму входа и предлагает сохранить
              её как учётные данные сайта. Значение скрываем через
              -webkit-text-security (см. styles.maskedInput).
            */}
            <StandardFormInput
              name={fields.emailPassword}
              schema={schema}
              inline={false}
              label={
                <span className="planr-input-label-help">
                  {getFieldLabel(fields.emailPassword, schema, null)} &nbsp;&nbsp;
                  <Tooltip
                    content={
                      <div>
                        <p>{`Перейдите в настройки почтового аккаунта -> Безопасность.`}</p>
                        <p>Создайте "Пароль для внешних приложений".</p>
                      </div>
                    }
                    position={Position.RIGHT}
                  >
                    <GeneralIcon type="general-question" />
                  </Tooltip>
                </span>
              }
              small={true}
              className="planr-form-input"
            >
              {({ field }) => (
                <InputGroup
                  id={field.name}
                  className={`planr-default-input ${styles.maskedInput}`}
                  {...field}
                  type="text"
                  autoComplete="off"
                  data-lpignore="true"
                />
              )}
            </StandardFormInput>
            <StandardFormInput
              name={fields.emailServer}
              schema={schema}
              inline={false}
              label={
                <span className="planr-input-label-help">
                  {getFieldLabel(fields.emailServer, schema, null)} &nbsp;&nbsp;
                  <Tooltip
                    content={
                      <div>
                        <p>{`Укажите имя smtp сервера от почтового сервиса (пример: smtp.yandex.ru).`}</p>
                      </div>
                    }
                    position={Position.RIGHT}
                  >
                    <GeneralIcon type="general-question" />
                  </Tooltip>
                </span>
              }
              small={true}
              className="planr-form-input"
            >
              {({ field }) => (
                <InputGroup
                  id={field.name}
                  className="planr-default-input"
                  {...field}
                  type="text"
                  autoComplete="off"
                  data-lpignore="true"
                />
              )}
            </StandardFormInput>
            <StandardFormInput
              name={fields.emailPort}
              schema={schema}
              inline={false}
              small={true}
              className="planr-form-input"
            >
              {({ field }) => (
                <InputGroup
                  id={field.name}
                  className="planr-default-input"
                  {...field}
                  type="text"
                  autoComplete="off"
                  onKeyPress={digits}
                  data-lpignore="true"
                />
              )}
            </StandardFormInput>
            <StandardFormInput
              name={fields.emailReadServer}
              schema={schema}
              inline={false}
              label={
                <span className="planr-input-label-help">
                  {getFieldLabel(fields.emailReadServer, schema, null)} &nbsp;&nbsp;
                  <Tooltip
                    content={
                      <div>
                        <p>{`Укажите имя imap сервера от почтового сервиса (пример: imap.yandex.ru).`}</p>
                      </div>
                    }
                    position={Position.RIGHT}
                  >
                    <GeneralIcon type="general-question" />
                  </Tooltip>
                </span>
              }
              small={true}
              className="planr-form-input"
            >
              {({ field }) => (
                <InputGroup
                  id={field.name}
                  className="planr-default-input"
                  {...field}
                  type="text"
                  autoComplete="off"
                  data-lpignore="true"
                />
              )}
            </StandardFormInput>
            <StandardFormInput
              name={fields.emailReadPort}
              schema={schema}
              inline={false}
              small={true}
              className="planr-form-input"
            >
              {({ field }) => (
                <InputGroup
                  id={field.name}
                  className="planr-default-input"
                  {...field}
                  type="text"
                  autoComplete="off"
                  onKeyPress={digits}
                  data-lpignore="true"
                />
              )}
            </StandardFormInput>
            <StandardFormInput
              name={fields.isSsl}
              schema={schema}
              small={true}
              className="planr-form-input"
              inline={false}
            >
              {({ field, form }) => (
                <PlanrButton
                  type="neutral"
                  icon={field.value ? "general-checkbox" : "general-zero-checkbox"}
                  onClick={() => {
                    form.setFieldValue(field.name, !field.value);
                    form.setFieldTouched(field.name, true);
                  }}
                  style={{ marginTop: "-5px" }}
                />
              )}
            </StandardFormInput>
            <PlanrButton type="dashed" onClick={toggleShowDialog} size="small" style={{ marginTop: "10px" }}>
              Отправить email
            </PlanrButton>

            <Dialog
              title={"Отправка электронного письма"}
              isOpen={showDialog}
              isCloseButtonShown={true}
              style={{ width: "600px" }}
              onClose={toggleShowDialog}
              backdropClassName="standard"
            >
              <div className={`${Classes.DIALOG_BODY} ${styles.dialogBody}`}>
                <div className={styles.label}>Email получателя</div>
                <InputGroup
                  className="planr-default-input"
                  value={emailTo}
                  onChange={(e: any) => {
                    setMail(e.currentTarget.value);
                  }}
                  autoComplete="off"
                  data-lpignore="true"
                />
                <div className={styles.label}>Тема письма</div>
                <InputGroup
                  className="planr-default-input"
                  value={topic}
                  onChange={(e: any) => {
                    setTopic(e.currentTarget.value);
                  }}
                  autoComplete="off"
                  data-lpignore="true"
                />
                <div className={styles.label}>Текст письма</div>
                <InputGroup
                  className="planr-default-input"
                  value={text}
                  onChange={(e: any) => {
                    setMessage(e.currentTarget.value);
                  }}
                  autoComplete="off"
                  data-lpignore="true"
                />
              </div>
              <div className={Classes.DIALOG_FOOTER}>
                <div className={Classes.DIALOG_FOOTER_ACTIONS}>
                  <PlanrButton
                    type="greenish"
                    onClick={async () => {
                      const res = await sendMailTest();
                      if (res) {
                        toggleShowDialog();
                      }
                    }}
                  >
                    Отправить
                  </PlanrButton>
                </div>
              </div>
            </Dialog>
          </div>

          <div className={styles.column} style={{ marginTop: "5px" }}>
            <StandardFormInput
              name={fields.showMinutes}
              schema={schema}
              small={true}
              className="planr-form-input"
              inline={false}
            >
              {({ field, form }) => (
                <PlanrButton
                  type="neutral"
                  icon={field.value ? "general-checkbox" : "general-zero-checkbox"}
                  onClick={() => {
                    form.setFieldValue(field.name, !field.value);
                    form.setFieldTouched(field.name, true);
                  }}
                  style={{ marginTop: "-5px" }}
                />
              )}
            </StandardFormInput>
            <StandardFormInput
              name={fields.prevDaysCount}
              schema={schema}
              small={true}
              className="planr-form-input"
              inline={false}
            >
              {({ field }) => (
                <InputGroup
                  id={field.name}
                  className="planr-default-input"
                  {...field}
                  type="number"
                  autoComplete="off"
                  data-lpignore="true"
                />
              )}
            </StandardFormInput>
            <StandardFormInput
              name={fields.futureDaysCount}
              schema={schema}
              small={true}
              className="planr-form-input"
              inline={false}
            >
              {({ field }) => (
                <InputGroup
                  id={field.name}
                  className="planr-default-input"
                  {...field}
                  type="number"
                  autoComplete="off"
                  data-lpignore="true"
                />
              )}
            </StandardFormInput>
          </div>
        </div>
      </div>
    );
  }

  toggleNetworksDialog = () => {
    this.setState({ showNetworksDialog: !this.state.showNetworksDialog, networksError: false });
  };

  setNetwork = (index: number, field: "address" | "comment", value: string) => {
    const networks = [...this.state.networks];
    networks[index] = { ...networks[index], [field]: value };
    this.setState({ networks });
  };

  removeNetwork = (index: number) => {
    const networks = [...this.state.networks];
    networks.splice(index, 1);
    this.setState({ networks });
  };

  addNetwork = () => {
    this.setState({ networks: [...this.state.networks, { address: "", comment: "" }] });
  };
}

export const SystemSettings = observer(SystemBase);
interface SystemState {
  showdialog: boolean;
  showDanger: boolean;
  bonusSalaryPercent: BonusSalaryPercentType[];
  employersExclude: DictionaryLinkType[];
  showNetworksDialog: boolean;
  networks: AutomaticNetworkSnapshotType[];
  networksError: boolean;
}
interface SystemSettingsProps {
  schema: Schema<any>;
  store: SettingsStoreType;
  showDialog: boolean;
  sendMailTest: () => Promise<boolean>;
  topic: string;
  text: string;
  emailTo: string;
  toggleShowDialog: () => void;
  setMail: (val: string) => void;
  setTopic: (val: string) => void;
  setMessage: (val: string) => void;
}
